<template>
    <!-- Sidebar -->
    <div class="relative h-full flex-shrink-0">
        <div class="h-full flex flex-col bg-gradient-to-r from-echoes-background-black to-echoes-background-gray/20 border-r border-echoes-border-gray/40 transition-all duration-300 ease-in-out relative overflow-hidden"
             :class="isCollapsed ? 'w-16' : 'w-72'">
                
                <!-- Header -->
                <div class="flex-shrink-0 h-16 flex items-center justify-end px-4 border-b border-echoes-border-gray/40">
                    <!-- Collapse Toggle -->
                    <button
                        @click="toggleCollapse"
                        class="w-8 h-8 text-white/80 hover:text-white flex items-center justify-center rounded-md hover:bg-white/10 transition-colors duration-200 flex-shrink-0"
                        :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                    >
                        <i class="uil uil-web-section text-lg"></i>
                    </button>
                </div>

                <!-- Navigation Content -->
                <div class="flex-1 overflow-y-auto overflow-x-hidden">
                    <nav class="py-4 space-y-6 transition-all duration-300 ease-in-out">
                        <div v-for="sectionData in sidebarSections" :key="sectionData.section" class="space-y-2">
                            <!-- Section Title -->
                            <div class="px-4 mb-2 transition-all duration-300 ease-in-out"
                                 :class="isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'">
                                <span class="text-xs uppercase text-echoes-primary font-semibold whitespace-nowrap">
                                    {{ sectionData.section }}
                                </span>
                            </div>
                            
                            <!-- Items in this section -->
                            <div class="space-y-1">
                                <div v-for="item in sectionData.items" :key="item.id" class="relative">
                                    <!-- Main item button -->
                                    <button
                                        @click="handleItemClick(item)"
                                        class="w-full p-2 transition-all hover:bg-white/5 duration-200 group relative flex items-center rounded-md mx-2"
                                        :class="{
                                            'text-echoes-primary bg-echoes-primary/10': isItemActive(item),
                                            'text-white/80 hover:text-white hover:bg-white/5': !isItemActive(item)
                                        }"
                                        :data-testid="'menu-item-' + item.id"
                                    >
                                        <!-- Item icon -->
                                        <div class="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                            <i :class="[
                                                item.icon,
                                                'transition-colors duration-200 text-lg',
                                                {
                                                    'text-echoes-primary': isItemActive(item),
                                                    'text-white/80 group-hover:text-white': !isItemActive(item)
                                                }
                                            ]"></i>
                                        </div>
                                        
                                        <!-- Item title and expand arrow -->
                                        <div class="ml-2 flex items-center justify-between flex-1 overflow-hidden transition-all duration-300 ease-in-out"
                                             :class="isCollapsed ? 'opacity-0 pointer-events-none w-0' : 'opacity-100'">
                                            <span class="font-medium text-sm whitespace-nowrap truncate"
                                                  :class="{
                                                      'text-echoes-primary': isItemActive(item),
                                                      'text-white/80': !isItemActive(item)
                                                  }">
                                                {{ item.title }}
                                            </span>
                                            
                                            <!-- Only show chevron if item has sub-items -->
                                            <i v-if="hasSubItems(item)" 
                                               class="uil uil-angle-down transition-transform duration-200 flex-shrink-0 ml-2"
                                               :class="{
                                                   'text-echoes-primary': isItemActive(item),
                                                   'text-white/80': !isItemActive(item),
                                                   'rotate-180': isItemExpanded(item.id)
                                               }"></i>
                                        </div>
                                    </button>

                                    <!-- Tooltip for collapsed state -->
                                    <div v-if="isCollapsed" 
                                         class="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-echoes-background-black text-white/80 hover:text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 border border-echoes-border-gray">
                                        {{ item.title }}
                                        <span v-if="hasSubItems(item)" class="ml-1 text-xs">(expand)</span>
                                    </div>

                                    <!-- Sub-items (expanded state) -->
                                    <div v-if="hasSubItems(item) && isItemExpanded(item.id) && !isCollapsed" 
                                         class="mt-1 overflow-hidden transition-all duration-300 ease-in-out">
                                        <div class="space-y-1 relative">
                                            <div class="absolute left-5 top-0 bottom-0 w-px bg-echoes-border-gray"></div>
                                            
                                            <button
                                                v-for="(subItem) in item.subItems"
                                                :key="subItem.id"
                                                @click="handleSubItemClick(subItem)"
                                                class="w-full pl-10 pr-3 py-2 rounded-md transition-all duration-200 group flex items-center mx-2"
                                                :class="{
                                                    'text-echoes-primary bg-echoes-primary/10': isSubItemActive(subItem),
                                                    'text-white/60 hover:text-white hover:bg-white/5': !isSubItemActive(subItem)
                                                }"
                                                :data-testid="'menu-item-' + subItem.id"
                                            >
                                                <!-- Sub-item icon -->
                                                <div class="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                                    <i :class="[
                                                        subItem.icon,
                                                        'transition-colors duration-200 text-sm',
                                                        {
                                                            'text-echoes-primary': isSubItemActive(subItem),
                                                            'text-white/60 group-hover:text-white': !isSubItemActive(subItem)
                                                        }
                                                    ]"></i>
                                                </div>
                                                
                                                <!-- Sub-item label -->
                                                <span class="ml-2.5 text-sm transition-colors duration-200 whitespace-nowrap truncate"
                                                      :class="{
                                                          'text-echoes-primary': isSubItemActive(subItem),
                                                          'text-white/60 group-hover:text-white': !isSubItemActive(subItem)
                                                      }">
                                                    {{ subItem.label }}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const currentRoute = computed(() => router.currentRoute.value);

const isCollapsed = ref(false);
const expandedItems = ref([]);

const sidebarSections = computed(() => {
    return [
        {
            section: 'main',
            items: [
                {
                    id: 'listings',
                    title: 'Listings',
                    icon: 'mdi mdi-view-grid-outline',
                    subItems: [
                        {
                            id: 'listings-list',
                            label: 'List',
                            icon: 'mdi mdi-view-grid-outline',
                            redirect: '/backoffice/listings',
                            routeName: 'listings-list'
                        },
                        {
                            id: 'listings-tags',
                            label: 'Tags',
                            icon: 'uil uil-tag',
                            redirect: '/backoffice/listings-tags',
                            routeName: 'listings-tags'
                        },
                        {
                            id: 'listings-tag-types',
                            label: 'Tag Types',
                            icon: 'uil uil-tag-alt',
                            redirect: '/backoffice/listings-tag-types',
                            routeName: 'listings-tag-types'
                        },
                        {
                            id: 'eval-llms',
                            label: 'Eval LLMs',
                            icon: 'uil uil-tag-alt',
                            redirect: '/backoffice/eval-llms',
                            routeName: 'eval-llms'
                        },
                        {
                            id: 'eval-agents',
                            label: 'Eval Agents',
                            icon: 'mdi mdi-robot-outline',
                            redirect: '/backoffice/eval-agents',
                            routeName: 'eval-agents'
                        },
                        {
                            id: 'licenses',
                            label: 'Data Licenses',
                            icon: 'uil uil-file-graph',
                            redirect: '/backoffice/licenses',
                            routeName: 'licenses'
                        },
                        {
                            id: 'payment-methods',
                            label: 'Payment Methods',
                            icon: 'uil uil-credit-card',
                            redirect: '/backoffice/payment-methods',
                            routeName: 'payment-methods'
                        }
                    ]
                },
                {
                    id: 'datasets',
                    title: 'Datasets',
                    icon: 'mdi mdi-view-grid-outline',
                    subItems: [
                        {
                            id: 'datasets-list',
                            label: 'List',
                            icon: 'mdi mdi-view-grid-outline',
                            redirect: '/backoffice/datasets',
                            routeName: 'datasets'
                        }
                    ]
                },
                {
                    id: 'rfps',
                    title: 'RFPs',
                    icon: 'mdi mdi-file-document-outline',
                    subItems: [
                        {
                            id: 'rfps-list',
                            label: 'List',
                            icon: 'mdi mdi-view-grid-outline',
                            redirect: '/backoffice/rfps',
                            routeName: 'rfps'
                        },
                        {
                            id: 'rfps-arbitration',
                            label: 'Arbitrations',
                            icon: 'uil uil-balance-scale',
                            redirect: '/backoffice/rfps-arbitration',
                            routeName: 'rfps-arbitration'
                        }
                    ]
                },
                {
                    id: 'sales',
                    title: 'Sales',
                    icon: 'uil uil-usd-circle',
                    subItems: [
                        {
                            id: 'sales-credentials',
                            label: 'Credentials',
                            icon: 'uil uil-postcard',
                            redirect: '/backoffice/sales/credentials',
                            routeName: 'sales-credentials'
                        }
                    ]
                },
                {
                    id: 'users',
                    title: 'Users',
                    icon: 'uil uil-users-alt',
                    subItems: [
                        {
                            id: 'users-list',
                            label: 'Users',
                            icon: 'uil uil-user',
                            redirect: '/backoffice/users',
                            routeName: 'users'
                        },
                        {
                            id: 'users-badges',
                            label: 'Badges',
                            icon: 'uil uil-award',
                            redirect: '/backoffice/users/badges',
                            routeName: 'users-badges'
                        },
                        {
                            id: 'groups',
                            label: 'Groups',
                            icon: 'uil uil-users-alt',
                            redirect: '/backoffice/groups',
                            routeName: 'groups'
                        },
                        {
                            id: 'flags',
                            label: 'Flags',
                            icon: 'mdi mdi-flag',
                            redirect: '/backoffice/flags',
                            routeName: 'flags'
                        },
                        {
                            id: 'requests',
                            label: 'Requests',
                            icon: 'mdi mdi-swap-horizontal-circle-outline',
                            redirect: '/backoffice/requests',
                            routeName: 'requests'
                        },
                        {
                            id: 'publication-requests',
                            label: 'Publication Requests',
                            icon: 'mdi mdi-file-check-outline',
                            redirect: '/backoffice/publication-requests',
                            routeName: 'publication-requests'
                        }
                    ]
                },
                {
                    id: 'actions',
                    title: 'Actions',
                    icon: 'mdi mdi-cog',
                    subItems: [
                        {
                            id: 'eval-runs-queue',
                            label: 'Eval Queue',
                            icon: 'uil uil-list-ul',
                            redirect: '/backoffice/eval-runs-queue',
                            routeName: 'eval-runs-queue'
                        },
                        {
                            id: 'cache-management',
                            label: 'Cache Management',
                            icon: 'mdi mdi-database',
                            redirect: '/backoffice/cache-management',
                            routeName: 'cache-management'
                        }
                    ]
                },
                {
                    id: 'files',
                    title: 'Files',
                    icon: 'mdi mdi-file-outline',
                    subItems: [
                        {
                            id: 'files-list',
                            label: 'List',
                            icon: 'mdi mdi-file-outline',
                            redirect: '/backoffice/files',
                            routeName: 'files'
                        },
                        {
                            id: 'files-accept',
                            label: 'Formats Accept',
                            icon: 'mdi mdi-format-list-bulleted',
                            redirect: '/backoffice/files/accept',
                            routeName: 'files-accept'
                        }
                    ]
                },
                {
                    id: 'system-settings',
                    title: 'System Settings',
                    icon: 'uil uil-setting',
                    redirect: '/backoffice/system-settings',
                    routeName: 'system-settings'
                }
            ]
        }
    ];
});

const hasSubItems = (item) => {
    return item.subItems && item.subItems.length > 0;
};
// Auto-expand items when their subitems are active
watch(currentRoute, (newRoute) => {
    sidebarSections.value.forEach(section => {
        section.items.forEach(item => {
            if (hasSubItems(item)) {
                const hasActiveSubItem = item.subItems.some(subItem => {
                    return subItem.routeName === newRoute.name;
                });
                if (hasActiveSubItem && !expandedItems.value.includes(item.id)) {
                    expandedItems.value.push(item.id);
                }
            }
        });
    });
}, { immediate: true });

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
};


const isItemExpanded = (itemId) => {
    return expandedItems.value.includes(itemId);
};

const toggleItem = (itemId) => {
    const index = expandedItems.value.indexOf(itemId);
    if (index > -1) {
        expandedItems.value.splice(index, 1);
    } else {
        expandedItems.value.push(itemId);
    }
};

const handleItemClick = (item) => {
    // If sidebar is collapsed, expand it first
    if (isCollapsed.value) {
        isCollapsed.value = false;
        // Wait for expansion animation before toggling item
        setTimeout(() => {
            if (hasSubItems(item)) {
                toggleItem(item.id);
            } else if (item.redirect) {
                router.push(item.redirect);
            }
        }, 300);
        return;
    }
    
    if (hasSubItems(item)) {
        toggleItem(item.id);
    } else if (item.redirect) {
        router.push(item.redirect);
    }
};

const handleSubItemClick = (subItem) => {
    // If sidebar is collapsed, expand it first
    if (isCollapsed.value) {
        isCollapsed.value = false;
        // Wait for expansion animation before navigating
        setTimeout(() => {
            if (subItem.redirect) {
                router.push(subItem.redirect);
            }
        }, 300);
        return;
    }
    
    if (subItem.redirect) {
        router.push(subItem.redirect);
    }
};

const isItemActive = (item) => {
    // Check if item itself is active
    if (item.routeName) {
        if (currentRoute.value.name === item.routeName || currentRoute.value.name?.startsWith(item.routeName)) {
            return true;
        }
    }
    
    // Check if any subitem is active
    if (hasSubItems(item)) {
        return item.subItems.some(subItem => {
            return subItem.routeName === currentRoute.value.name;
        });
    }
    
    return false;
};

const isSubItemActive = (subItem) => {
    if (subItem.routeName) {
        return currentRoute.value.name === subItem.routeName;
    }
    return false;
};

</script>

<style scoped>
:deep(svg) {
    fill: currentColor;
    stroke: currentColor;
}

:deep(svg *) {
    fill: inherit;
    stroke: inherit;
}
</style>
